/**
 * 系统检测工具类
 * 用于检测用户的操作系统和CPU架构
 */

export type SystemType = 'mac' | 'windows' | 'linux' | 'unknown';
export type ChipType = 'intel' | 'apple' | 'unknown';

export interface SystemInfo {
  system: SystemType;
  chip: ChipType;
  userAgent: string;
  platform: string;
  cpuArchitecture: string;
  isAppleSilicon: boolean;
  detectionMethod: string[];
}

export interface DetailedChipInfo {
  type: ChipType;
  architecture: string;
  detectionMethods: string[];
  confidence: 'high' | 'medium' | 'low';
}

/**
 * 检测用户操作系统类型
 * @returns SystemType 系统类型
 */
export function detectSystemType(): SystemType {
  const platform = navigator.platform.toLowerCase();
  const userAgent = navigator.userAgent.toLowerCase();

  // 检测 macOS
  if (platform.includes('mac') || userAgent.includes('mac')) {
    return 'mac';
  }

  // 检测 Windows
  if (platform.includes('win') || userAgent.includes('windows')) {
    return 'windows';
  }

  // 检测 Linux
  if (platform.includes('linux') || userAgent.includes('linux')) {
    return 'linux';
  }

  return 'unknown';
}

/**
 * 检测Mac芯片类型（仅适用于Mac系统）
 * @returns ChipType 芯片类型
 */
export function detectMacChipType(): ChipType {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;

  // 1. 检查 User Agent 中的 ARM64 标识
  if (userAgent.includes('ARM64')) {
    return 'apple';
  }

  // 2. 检查平台字符串中的 ARM 标识
  if (platform.includes('ARM') || platform.includes('arm')) {
    return 'apple';
  }

  // 3. 检查新的 userAgentData API (Client Hints)
  if ('userAgentData' in navigator) {
    const userAgentData = (navigator as any).userAgentData;

    // 检查架构信息
    if (userAgentData?.platform === 'macOS' && userAgentData?.architecture === 'arm') {
      return 'apple';
    }

    // 检查平台信息
    if (userAgentData?.platform === 'macOS ARM64') {
      return 'apple';
    }
  }

  // 4. WebGL 回退检测（仅在浏览器环境中可用）
  if (typeof window !== 'undefined') {
    try {
      const result = detectWithWebGL();
      if (result === 'apple') {
        return 'apple';
      }
    } catch {
      // WebGL 检测失败，继续其他检测方法
    }
  }

  // 5. 检查 CPU 架构相关信息
  const cpuInfo = getCPUArchitecture();
  if (cpuInfo === 'arm' || cpuInfo === 'arm64') {
    return 'apple';
  }

  // 6. 默认情况下，如果是Mac但没有检测到ARM，则认为是Intel
  if (platform.includes('Mac') || platform.includes('Intel') || userAgent.includes('Intel')) {
    return 'intel';
  }

  return 'unknown';
}

// WebGL上下文缓存，避免重复创建
let webglContextCache: {
  canvas?: HTMLCanvasElement;
  context?: WebGLRenderingContext | WebGL2RenderingContext;
  renderer?: string;
  lastUsed?: number;
} = {};

/**
 * 安全地获取WebGL渲染器信息，避免创建过多上下文
 * @returns string | null 渲染器信息
 */
function getWebGLRenderer(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const now = Date.now();
  const CACHE_DURATION = 5 * 60 * 1000; // 5分钟缓存

  // 检查缓存是否有效
  if (
    webglContextCache.renderer &&
    webglContextCache.lastUsed &&
    now - webglContextCache.lastUsed < CACHE_DURATION
  ) {
    return webglContextCache.renderer;
  }

  try {
    // 清理旧的上下文
    if (webglContextCache.context && webglContextCache.canvas) {
      const ext = webglContextCache.context.getExtension('WEBGL_lose_context');
      if (ext) {
        ext.loseContext();
      }
      webglContextCache.canvas.remove();
    }

    // 创建新的canvas和上下文
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    canvas.style.display = 'none';

    const webgl =
      canvas.getContext('webgl2', { failIfMajorPerformanceCaveat: true }) ||
      canvas.getContext('webgl', { failIfMajorPerformanceCaveat: true }) ||
      canvas.getContext('experimental-webgl', { failIfMajorPerformanceCaveat: true });

    if (!webgl) {
      return null;
    }

    const debug = webgl.getExtension('WEBGL_debug_renderer_info');
    let renderer = null;

    if (debug) {
      renderer = webgl.getParameter(debug.UNMASKED_RENDERER_WEBGL);
    }

    // 更新缓存
    webglContextCache = {
      canvas,
      context: webgl,
      renderer,
      lastUsed: now,
    };

    // 设置定时器清理上下文
    setTimeout(() => {
      if (webglContextCache.context && webglContextCache.canvas) {
        const ext = webglContextCache.context.getExtension('WEBGL_lose_context');
        if (ext) {
          ext.loseContext();
        }
        webglContextCache.canvas.remove();
        webglContextCache = {};
      }
    }, CACHE_DURATION);

    return renderer;
  } catch (error) {
    console.warn('WebGL检测失败:', error);
    return null;
  }
}

/**
 * 使用WebGL检测Apple Silicon
 * @returns ChipType | null
 */
function detectWithWebGL(): ChipType | null {
  const renderer = getWebGLRenderer();
  if (renderer && renderer.match(/apple m\d/i)) {
    return 'apple';
  }
  return null;
}

/**
 * 获取CPU架构信息
 * @returns string CPU架构
 */
function getCPUArchitecture(): string {
  // 检查 navigator.platform
  const platform = navigator.platform.toLowerCase();

  if (platform.includes('arm') || platform.includes('aarch64')) {
    return 'arm64';
  }

  if (platform.includes('x86_64') || platform.includes('amd64')) {
    return 'x64';
  }

  if (platform.includes('i386') || platform.includes('i686')) {
    return 'x86';
  }

  // 检查 userAgentData
  if ('userAgentData' in navigator) {
    const userAgentData = (navigator as any).userAgentData;
    if (userAgentData?.architecture) {
      return userAgentData.architecture.toLowerCase();
    }
  }

  return 'unknown';
}

/**
 * 高级 Apple Silicon 检测（综合多种方法）
 * @returns boolean 是否为 Apple Silicon
 */
export function isAppleSilicon(): boolean {
  // 首先确认是 macOS
  if (detectSystemType() !== 'mac') {
    return false;
  }

  // 使用芯片检测函数
  const chipType = detectMacChipType();
  return chipType === 'apple';
}

/**
 * 获取详细的芯片信息（仅适用于Mac系统）
 * @returns DetailedChipInfo 详细芯片信息
 */
export function getDetailedMacChipInfo(): DetailedChipInfo {
  const detectionMethods: string[] = [];
  let chipType: ChipType = 'unknown';
  let confidence: 'high' | 'medium' | 'low' = 'low';

  const userAgent = navigator.userAgent;
  const platform = navigator.platform;

  // 检测方法1: User Agent ARM64
  if (userAgent.includes('ARM64')) {
    detectionMethods.push('UserAgent-ARM64');
    chipType = 'apple';
    confidence = 'high';
  }

  // 检测方法2: Platform ARM
  if (platform.includes('ARM') || platform.includes('arm')) {
    detectionMethods.push('Platform-ARM');
    chipType = 'apple';
    confidence = 'high';
  }

  // 检测方法3: Client Hints
  if ('userAgentData' in navigator) {
    const userAgentData = (navigator as any).userAgentData;
    if (userAgentData?.architecture === 'arm') {
      detectionMethods.push('ClientHints-Architecture');
      chipType = 'apple';
      confidence = 'high';
    }
  }

  // 检测方法4: WebGL
  if (typeof window !== 'undefined' && chipType === 'unknown') {
    try {
      const result = detectWithWebGL();
      if (result === 'apple') {
        detectionMethods.push('WebGL-Renderer');
        chipType = 'apple';
        confidence = 'medium';
      }
    } catch {
      // WebGL 检测失败
    }
  }

  // 如果没有检测到 Apple Silicon，且是 Mac，则认为是 Intel
  if (chipType === 'unknown' && (platform.includes('Mac') || userAgent.includes('Mac'))) {
    chipType = 'intel';
    detectionMethods.push('Default-Intel');
    confidence = 'medium';
  }

  return {
    type: chipType,
    architecture: getCPUArchitecture(),
    detectionMethods,
    confidence,
  };
}

/**
 * 获取完整的系统信息
 * @returns SystemInfo 完整的系统信息
 */
export function getSystemInfo(): SystemInfo {
  const system = detectSystemType();
  const chip = system === 'mac' ? detectMacChipType() : 'unknown';
  const cpuArchitecture = getCPUArchitecture();
  const appleSilicon = isAppleSilicon();

  // 获取检测方法
  const detectionMethods: string[] = [];
  if (system === 'mac') {
    const detailedInfo = getDetailedMacChipInfo();
    detectionMethods.push(...detailedInfo.detectionMethods);
  }

  return {
    system,
    chip,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    cpuArchitecture,
    isAppleSilicon: appleSilicon,
    detectionMethod: detectionMethods,
  };
}

/**
 * 获取推荐下载类型
 * @returns 'apple' | 'intel' | 'windows' | 'linux' | null
 */
export function getRecommendedDownloadType(): 'apple' | 'intel' | 'windows' | 'linux' | null {
  const { system, chip } = getSystemInfo();

  switch (system) {
    case 'mac':
      return chip === 'apple' ? 'apple' : 'intel';
    case 'windows':
      return 'windows';
    case 'linux':
      return 'linux';
    default:
      return null;
  }
}

/**
 * 判断是否支持当前系统
 * @returns boolean 是否支持
 */
export function isSystemSupported(): boolean {
  const { system } = getSystemInfo();
  // 目前只支持 Mac 和 Windows
  return system === 'mac' || system === 'windows';
}

/**
 * 获取系统友好显示名称
 * @param system 系统类型
 * @param chip 芯片类型（可选）
 * @returns string 友好显示名称
 */
export function getSystemDisplayName(system: SystemType, chip?: ChipType): string {
  switch (system) {
    case 'mac':
      if (chip === 'apple') {
        return 'macOS (Apple Silicon)';
      }
      if (chip === 'intel') {
        return 'macOS (Intel)';
      }
      return 'macOS';
    case 'windows':
      return 'Windows';
    case 'linux':
      return 'Linux';
    default:
      return '未知系统';
  }
}

/**
 * 获取芯片的详细显示名称
 * @param chip 芯片类型
 * @param architecture CPU架构
 * @returns string 详细显示名称
 */
export function getChipDisplayName(chip: ChipType, architecture?: string): string {
  switch (chip) {
    case 'apple':
      return `Apple Silicon (${architecture || 'ARM64'})`;
    case 'intel':
      return `Intel (${architecture || 'x64'})`;
    default:
      return `未知芯片 (${architecture || 'unknown'})`;
  }
}

/**
 * 检查是否为特定的 Apple Silicon 型号
 * @param model 要检查的型号 (如 'M1', 'M2', 'M3')
 * @returns boolean 是否匹配
 */
export function isSpecificAppleChip(model: string): boolean {
  if (!isAppleSilicon()) {
    return false;
  }

  // 通过 WebGL 检测具体型号
  if (typeof window !== 'undefined') {
    try {
      const renderer = getWebGLRenderer();
      if (renderer) {
        const regex = new RegExp(`apple ${model.toLowerCase()}`, 'i');
        return regex.test(renderer);
      }
    } catch {
      // 检测失败
    }
  }

  return false;
}

/**
 * 获取系统兼容性信息
 * @returns object 兼容性信息
 */
export function getSystemCompatibility() {
  const systemInfo = getSystemInfo();

  return {
    isSupported: isSystemSupported(),
    recommendedDownload: getRecommendedDownloadType(),
    systemInfo,
    warnings: getSystemWarnings(systemInfo),
  };
}

/**
 * 获取系统相关的警告信息
 * @param systemInfo 系统信息
 * @returns string[] 警告信息数组
 */
function getSystemWarnings(systemInfo: SystemInfo): string[] {
  const warnings: string[] = [];

  // 检查是否为不支持的系统
  if (!isSystemSupported()) {
    warnings.push('当前系统可能不被完全支持');
  }

  // 检查芯片检测的可靠性
  if (systemInfo.system === 'mac') {
    const detailedInfo = getDetailedMacChipInfo();
    if (detailedInfo.confidence === 'low') {
      warnings.push('芯片类型检测可靠性较低，建议手动确认');
    }
  }

  // 检查是否缺少检测方法
  if (systemInfo.detectionMethod.length === 0) {
    warnings.push('无法获取详细的系统信息');
  }

  return warnings;
}

/**
 * 清理WebGL上下文缓存
 */
export function cleanupWebGLContext(): void {
  if (webglContextCache.context && webglContextCache.canvas) {
    try {
      const ext = webglContextCache.context.getExtension('WEBGL_lose_context');
      if (ext) {
        ext.loseContext();
      }
      webglContextCache.canvas.remove();
    } catch (error) {
      console.warn('清理WebGL上下文失败:', error);
    }
    webglContextCache = {};
  }
}

/**
 * 初始化系统检测器，设置清理监听器
 */
export function initSystemDetector(): void {
  if (typeof window !== 'undefined') {
    // 页面卸载时清理WebGL上下文
    window.addEventListener('beforeunload', cleanupWebGLContext);
    window.addEventListener('pagehide', cleanupWebGLContext);

    // 页面隐藏时也清理，避免在Safari中的问题
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        cleanupWebGLContext();
      }
    });
  }
}

/**
 * 调试用：打印详细的系统检测信息
 */
export function debugSystemInfo(): void {
  const systemInfo = getSystemInfo();
  const compatibility = getSystemCompatibility();

  console.group('🔍 系统检测详细信息');
  console.log('基础信息:', {
    system: systemInfo.system,
    chip: systemInfo.chip,
    architecture: systemInfo.cpuArchitecture,
    isAppleSilicon: systemInfo.isAppleSilicon,
  });

  console.log('检测方法:', systemInfo.detectionMethod);
  console.log('User Agent:', systemInfo.userAgent);
  console.log('Platform:', systemInfo.platform);

  if (systemInfo.system === 'mac') {
    const detailedInfo = getDetailedMacChipInfo();
    console.log('详细芯片信息:', detailedInfo);

    // 测试特定芯片型号
    const chipModels = ['M1', 'M2', 'M3'];
    chipModels.forEach((model) => {
      console.log(`是否为 ${model}:`, isSpecificAppleChip(model));
    });
  }

  console.log('兼容性信息:', compatibility);
  console.groupEnd();
}
